/*
 * Product page personalisation for the "Line item property" block:
 *   - Image choice: keeps the hidden line item property in sync with the picked design(s).
 *     With multiple choice on, any combination can be picked (default included); clearing
 *     them all brings the default back. A locked default is always included.
 *     Designs linked to an add-on product add it to the cart.
 *   - Finish time: digits-only input with a live preview, sent with its colour only once complete.
 *     When a digit collection is set, each digit's product is added to the cart.
 *
 * Add-ons ride along with the theme's own add to cart request as items[n][...] fields,
 * which Shopify's /cart/add.js accepts next to the main product's id/quantity fields.
 *
 * Loaded by each block that needs it, so it guards against running twice.
 */
(function () {
  if (window.productPersonalisation) return;
  window.productPersonalisation = true;

  function readJson(element, fallback) {
    try {
      return element ? JSON.parse(element.textContent) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  // Image choice
  // ---------------------------------------------------------------------------
  function updateImageChoice(fieldset, changed) {
    const defaultInput = fieldset.querySelector('[data-image-choice-default]');
    const options = [...fieldset.querySelectorAll('.image-choice__item input')];

    // A locked default (always included) can't be unticked
    if (defaultInput.hasAttribute('data-image-choice-locked')) defaultInput.checked = true;
    if (changed && !options.some((input) => input.checked)) defaultInput.checked = true;

    const picked = options.filter((input) => input.checked).map((input) => input.value);
    fieldset.querySelector('[data-image-choice-value]').value = picked.join(', ') || defaultInput.value;
  }

  // Finish time
  // ---------------------------------------------------------------------------
  function finishTimeData(block) {
    if (!block._data) {
      block._data = {
        images: readJson(block.querySelector('[data-finish-time-digits]'), {}),
        products: readJson(block.querySelector('[data-finish-time-products]'), null),
      };
    }
    return block._data;
  }

  function updateFinishTime(block) {
    const input = block.querySelector('[data-finish-time-input]');
    const digits = input.value.replace(/\D/g, '').slice(0, Number(block.dataset.digits));
    if (input.value !== digits) input.value = digits;

    const { images, products } = finishTimeData(block);
    const colourInput = block.querySelector('[data-finish-time-colour]:checked') || block.querySelector('[data-finish-time-colour]');
    const colour = colourInput ? colourInput.value : '';

    input.setCustomValidity('');
    let valid = input.checkValidity();
    if (!valid) {
      input.setCustomValidity(block.dataset.error || '');
    } else if (products && digits) {
      const missing = [...digits].find((digit) => !products[digit] || !products[digit].available);
      if (missing !== undefined) {
        valid = false;
        input.setCustomValidity((block.dataset.unavailableError || '').replace('[digit]', missing));
      }
    }
    const complete = valid && digits.length > 0;

    const timeValue = block.querySelector('[data-finish-time-value]');
    const colourValue = block.querySelector('[data-finish-time-colour-value]');
    timeValue.value = digits;
    colourValue.value = colour;
    timeValue.disabled = !complete;
    colourValue.disabled = !complete || !colour;

    const preview = block.querySelector('[data-finish-time-preview]');
    const urls = images[colour];
    preview.hidden = !digits || !urls;
    if (urls) {
      preview.replaceChildren(
        ...[...digits].map((digit) => {
          const img = document.createElement('img');
          img.src = urls[digit];
          img.alt = '';
          return img;
        })
      );
    }

    const total = block.querySelector('[data-finish-time-total]');
    if (total && products) {
      const cents = complete ? [...digits].reduce((sum, digit) => sum + products[digit].price, 0) : 0;
      total.hidden = !cents;
      total.textContent = (block.dataset.totalText || '')
        .replace('[count]', digits.length)
        .replace('[total]', formatMoney(cents, block.dataset.currency));
    }
  }

  // Add to cart
  // ---------------------------------------------------------------------------
  function blocksFor(selector, formId) {
    return [...document.querySelectorAll(`${selector}[data-form-id="${formId}"]`)];
  }

  function variantLabel(formId, variantId) {
    const block = document.querySelector(`[data-product-variants][data-form-id="${formId}"]`);
    const data = readJson(block, null);
    if (!data) return '';
    const variant = data.variants[variantId];
    return variant ? `${data.title} - ${variant}` : data.title;
  }

  function addOnItems(form, formId) {
    const formData = new FormData(form);
    const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    const forLabel = variantLabel(formId, formData.get('id'));
    const items = [];

    blocksFor('[data-image-choice]', formId).forEach((fieldset) => {
      fieldset.querySelectorAll('input[data-addon-variant]:checked').forEach((input) => {
        items.push({ id: input.dataset.addonVariant, quantity, properties: { For: forLabel } });
      });
    });

    blocksFor('[data-finish-time]', formId).forEach((block) => {
      const timeValue = block.querySelector('[data-finish-time-value]');
      const { products } = finishTimeData(block);
      if (!products || timeValue.disabled || !timeValue.value) return;

      const counts = {};
      [...timeValue.value].forEach((digit) => (counts[digit] = (counts[digit] || 0) + 1));
      Object.entries(counts).forEach(([digit, count]) => {
        items.push({
          id: products[digit].id,
          quantity: count * quantity,
          properties: { For: `${forLabel} (finish time ${timeValue.value})` },
        });
      });
    });

    return items;
  }

  function writeAddOnInputs(formId, items) {
    let holder = document.querySelector(`[data-addon-inputs="${formId}"]`);
    if (!holder) {
      holder = document.createElement('div');
      holder.hidden = true;
      holder.dataset.addonInputs = formId;
      document.body.appendChild(holder);
    }

    const field = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('form', formId);
      return input;
    };

    holder.replaceChildren(
      ...items.flatMap((item, index) => [
        field(`items[${index}][id]`, item.id),
        field(`items[${index}][quantity]`, item.quantity),
        ...Object.entries(item.properties).map(([key, value]) => field(`items[${index}][properties][${key}]`, value)),
      ])
    );
  }

  // Runs before the theme's add to cart handler (which skips native form validation and
  // posts the form's FormData), so it can stop an invalid finish time and add the add-ons.
  function beforeAddToCart(event) {
    const button = event.target.closest && event.target.closest('[data-add-to-cart]');
    if (!button || !button.form) return;

    // getAttribute: the form's variant input is named "id", which shadows form.id
    const formId = button.form.getAttribute('id');

    for (const block of blocksFor('[data-finish-time]', formId)) {
      updateFinishTime(block);
      const input = block.querySelector('[data-finish-time-input]');
      if (!input.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.reportValidity();
        input.focus();
        return;
      }
    }

    writeAddOnInputs(formId, addOnItems(button.form, formId));
  }

  // Wiring
  // ---------------------------------------------------------------------------
  function init(root) {
    root.querySelectorAll('[data-image-choice]').forEach((fieldset) => updateImageChoice(fieldset));
    root.querySelectorAll('[data-finish-time]').forEach((block) => updateFinishTime(block));
  }

  document.addEventListener('change', (event) => {
    const fieldset = event.target.closest('[data-image-choice]');
    if (fieldset) updateImageChoice(fieldset, event.target);

    const block = event.target.closest('[data-finish-time]');
    if (block) updateFinishTime(block);
  });

  document.addEventListener('input', (event) => {
    const block = event.target.closest('[data-finish-time]');
    if (block) updateFinishTime(block);
  });

  document.addEventListener('click', beforeAddToCart, true);

  // Theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  init(document);
})();
